import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Grid, GridProps, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { useModal } from 'mui-modal-provider';

import Button from '../../../components/Button/Button';
import ChipOptions, { ChipOptionsProps } from '../../../components/ChipOptions/ChipOptions';
import { useConfirm } from '../../../components/Dialogs/Confirm/context/ConfirmDialog.context';
import ManageContentDialog from '../../../components/Dialogs/ManageContent/ManageContentDialog';
import InfiniteScroll from '../../../components/InfiniteScroll/InfiniteScroll';
import { useNotificationsContext } from '../../../contexts/NotificationsContext';
import {
  VenueListReservationsContentFilter,
  VenueReservationContentItemV2,
} from '../../../graphql/API';
import useListReservationContentQuery from '../../../hooks/reactQuery/useListReservationContentQuery';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { extractSubscriptionPlan } from '../../SubscriptionPlans/common/helper';
import Content from './Content';
import ContentSkeleton from './ContentSkeleton';
import { EmptyState } from './EmptyState';
import { PurchasePostsCard } from './PurchasePostsCard';

type OnClick = (item: VenueReservationContentItemV2) => void;
const commonGrid = (key: string): GridProps => ({ item: true, key, xs: 2.4 });

const ListContent: React.FC = () => {
  const { showModal } = useModal();
  const { confirm } = useConfirm();

  const navigate = useNavigate();

  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isFreeVenue =
    (venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans) &&
      (!subscriptionPlan || subscriptionPlan === 'basic')) ||
    ((venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) ||
      venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) &&
      (!subscriptionPlan || subscriptionPlan === 'basic'));

  const { handleUpdateContent } = useNotificationsContext() || {};

  const [filters, setFilters] = React.useState<
    Partial<VenueListReservationsContentFilter> | undefined
  >({ byVenue: false, isPost: true });

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useListReservationContentQuery(filters, 50, filters?.isPost ? !isFreeVenue : true);

  React.useEffect(() => {
    if (data.length > 0 && handleUpdateContent) {
      handleUpdateContent(data[0].createdAt || '');
    }
  }, [data]);

  const handleChipChanged = React.useCallback<ChipOptionsProps['onChange']>(
    ({ value: v }) => {
      const downloaded = v === 'purchased';
      setFilters((x) => ({
        ...x,
        byVenue: downloaded,
        ...(!downloaded ? { isPost: v === 'is-post' } : { isPost: undefined }),
      }));
    },
    [setFilters],
  );

  const handleClick = React.useCallback<OnClick>(
    (item) => {
      showModal(ManageContentDialog, {
        items: data,
        itemId: item.id ?? 0,
        confirm,
      });
    },
    [showModal, confirm, data],
  );

  const renderContent = React.useCallback(() => {
    if (isLoading) {
      return [...Array(8)].map((x, i) => (
        <Grid {...commonGrid(`grid-skeleton-${i}`)}>
          <ContentSkeleton />
        </Grid>
      ));
    }

    if (isFreeVenue && filters?.isPost && !filters?.byVenue) {
      return (
        <Grid {...commonGrid(`grid-empty`)}>
          <PurchasePostsCard />
        </Grid>
      );
    } else if (data.length === 0) {
      return (
        <Grid {...commonGrid(`grid-empty`)} xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 502px)',
            }}
          >
            <EmptyState
              type={
                filters?.isPost && !filters?.byVenue
                  ? 'post'
                  : !filters?.byVenue && !filters?.isPost
                    ? 'extra-content'
                    : filters.byVenue
                      ? 'purchased'
                      : 'post'
              }
              isBrand={venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)}
              productHandle={() => {
                if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)) {
                  navigate('/products/create');
                } else {
                  navigate('/offers/create');
                }
              }}
              inviteHandle={() => {
                navigate('/collabs');
              }}
              buyExtraContentHandle={() => {
                setFilters((prev) => ({ ...prev, isPost: false, byVenue: false }));
              }}
            />
          </Box>
        </Grid>
      );
    }

    return data.map((x) => (
      <Grid {...commonGrid(`grid-content-${x.id}`)}>
        <Content
          onClick={() => handleClick(x)}
          src={x.images?.small ?? ''}
          video={x.images?.video ?? undefined}
          userAvatar={x.user?.picture?.thumbnail ?? ''}
          name={`${x.user?.firstname} ${x.user?.lastname}`}
          data={x}
        />
      </Grid>
    ));
  }, [
    isLoading,
    data,
    isFreeVenue,
    filters?.isPost,
    filters?.byVenue,
    venueState?.tags,
    navigate,
    handleClick,
  ]);

  return (
    <Box>
      <ChipOptions
        defaultValue={
          filters?.isPost && !filters?.byVenue
            ? 'is-post'
            : !filters?.byVenue && !filters?.isPost
              ? 'not-post'
              : filters.byVenue
                ? 'purchased'
                : 'is-post'
        }
        name='contentDownloaded'
        options={[
          { label: 'Posts', value: 'is-post' },
          { label: 'Extra content', value: 'not-post' },
          { label: 'Purchased', value: 'purchased' },
        ]}
        onChange={handleChipChanged}
      />

      <Box sx={{ pt: 4 }}>
        <InfiniteScroll
          isLoading={isLoading}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
        >
          <Grid container spacing={2}>
            {renderContent()}
          </Grid>
        </InfiniteScroll>
      </Box>
    </Box>
  );
};

export default ListContent;
